import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Printer } from './types';

interface PrinterCardProps {
  printer: Printer;
  onDelete?: (printerId: string) => Promise<void>;
}

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' => {
  const value = status.toLowerCase();
  if (value === 'online') return 'success';
  if (value === 'offline') return 'warning';
  if (value === 'error') return 'error';
  return 'default';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const PrinterCard: React.FC<PrinterCardProps> = ({ printer, onDelete }) => {
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    
    try {
      setDeleting(true);
      await onDelete(printer.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: (theme) => '1px solid ' + theme.palette.divider,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: (theme) => theme.shadows[3],
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {printer.display_name}
              </Typography>
              {printer.queue_name && (
                <Typography variant="body2" color="text.secondary">
                  Queue: {printer.queue_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={printer.status.toUpperCase()}
                color={statusColor(printer.status)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              {onDelete && (
                <Tooltip title={t('printing.deleteButton')}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ ml: 1 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Divider />

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Printer ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {printer.id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Last seen
              </Typography>
              <Typography variant="body2">
                {formatDateTime(printer.last_seen_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {formatDateTime(printer.created_at)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('printing.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('printing.deleteConfirmMessage', { name: printer.display_name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            {t('translation.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? t('printing.deleting') : t('printing.deleteButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrinterCard;
